import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { NASELJA, PODLOGE } from '../lib/konstante';
import Ikona from './Ikona';

/** Karta samo prikazuje lokacije. Pinovi i klikovi ne mijenjaju koordinate. */
export default function Karta({
  tocke,
  odabraniId,
  naOdabir,
  bojaTocke,
  velicinaTocke,
}) {
  const spremnik = useRef(null);
  const karta = useRef(null);
  const slojPinova = useRef(null);
  const [aktivnaPodloga, postaviAktivnuPodlogu] = useState('osm');
  const [greskaPodloge, postaviGreskuPodloge] = useState(false);
  const smjestene = tocke.filter((t) => t.lat != null && t.lng != null);

  useEffect(() => {
    const m = L.map(spremnik.current, { zoomControl: false });
    m.fitBounds(
      NASELJA.map((n) => n.ll),
      { padding: [45, 45] },
    );
    L.control
      .zoom({
        position: 'topright',
        zoomInTitle: 'Približi kartu',
        zoomOutTitle: 'Udalji kartu',
      })
      .addTo(m);
    L.control.scale({ position: 'bottomright', imperial: false }).addTo(m);
    NASELJA.forEach((n) => {
      L.marker(n.ll, {
        icon: L.divIcon({
          className: '',
          iconSize: [8, 8],
          iconAnchor: [4, 4],
          html: '<div class="sredina-naselja"></div>',
        }),
        interactive: false,
        keyboard: false,
        zIndexOffset: -1000,
      })
        .addTo(m)
        .bindTooltip(n.naziv, {
          className: 'tt oznaka-naselja',
          permanent: true,
          direction: 'right',
          offset: [8, 0],
        });
    });
    slojPinova.current = L.layerGroup().addTo(m);
    karta.current = m;
    const promjenaVelicine = new ResizeObserver(() => m.invalidateSize());
    promjenaVelicine.observe(spremnik.current);
    return () => {
      promjenaVelicine.disconnect();
      m.remove();
      karta.current = null;
      slojPinova.current = null;
    };
  }, []);

  useEffect(() => {
    if (!karta.current) return;
    const m = karta.current;
    const definicija = PODLOGE.find((p) => p.kljuc === aktivnaPodloga);
    postaviGreskuPodloge(false);
    const sloj =
      definicija.tip === 'wms'
        ? L.tileLayer.wms(definicija.url, definicija.opcije)
        : L.tileLayer(definicija.url, definicija.opcije);
    let pogresaka = 0;
    sloj.on('tileerror', () => {
      if (++pogresaka >= 3) postaviGreskuPodloge(true);
    });
    sloj.addTo(m).bringToBack();
    return () => {
      sloj.off();
      if (m.hasLayer(sloj)) m.removeLayer(sloj);
    };
  }, [aktivnaPodloga]);

  useEffect(() => {
    if (!slojPinova.current) return;
    slojPinova.current.clearLayers();
    tocke.forEach((t) => {
      if (t.lat == null || t.lng == null) return;
      const aktivan = t.id === odabraniId;
      const velicina = (velicinaTocke?.(t) ?? 15) + (aktivan ? 5 : 0);
      const pin = L.marker([t.lat, t.lng], {
        draggable: false,
        title: t.naslov,
        alt: t.naslov,
        icon: L.divIcon({
          className: '',
          iconSize: [velicina, velicina],
          iconAnchor: [velicina / 2, velicina / 2],
          html: `<div class="pin${aktivan ? ' pin-aktivan' : ''}" style="background:${bojaTocke(t)}"></div>`,
        }),
      });
      if (t.naslov) {
        // Leaflet string tumači kao HTML; podaci iz baze smiju biti samo tekst.
        const tekst = document.createElement('span');
        tekst.textContent = t.naslov;
        pin.bindTooltip(tekst, { className: 'tt' });
      }
      pin.on('click', () => naOdabir(t.id));
      slojPinova.current.addLayer(pin);
    });
  }, [tocke, odabraniId, naOdabir, bojaTocke, velicinaTocke]);

  useEffect(() => {
    if (!karta.current) return;
    const smjesteneTocke = tocke.filter((t) => t.lat != null && t.lng != null);
    karta.current.fitBounds(
      smjesteneTocke.length
        ? smjesteneTocke.map((t) => [t.lat, t.lng])
        : NASELJA.map((n) => n.ll),
      { padding: [45, 45], maxZoom: 16 },
    );
  }, [tocke]);

  useEffect(() => {
    const t = tocke.find((z) => z.id === odabraniId);
    if (!karta.current || t?.lat == null) return;
    const m = karta.current;
    const pomak = window.matchMedia('(min-width: 701px)').matches
      ? Math.min(130, m.getSize().x / 4)
      : 0;
    const srediste = m.unproject(m.project([t.lat, t.lng]).add([pomak, 0]));
    m.panTo(srediste);
  }, [odabraniId, tocke]);

  const prikaziSve = () => {
    karta.current?.fitBounds(
      smjestene.length
        ? smjestene.map((t) => [t.lat, t.lng])
        : NASELJA.map((n) => n.ll),
      { padding: [45, 45], maxZoom: 17 },
    );
  };
  return (
    <div className="karta-okvir">
      <div
        ref={spremnik}
        className="karta"
        role="region"
        aria-label="Karta lokacija priključaka"
      />
      <div className="podloge" aria-label="Podloga karte">
        {PODLOGE.map((p) => (
          <button
            key={p.kljuc}
            type="button"
            aria-pressed={aktivnaPodloga === p.kljuc}
            title={p.opis ?? p.naziv}
            onClick={() => postaviAktivnuPodlogu(p.kljuc)}
          >
            {p.naziv}
          </button>
        ))}
      </div>
      <button
        className="obuhvat-karte"
        onClick={prikaziSve}
        aria-label="Prikaži sve vidljive točke"
        title="Prikaži sve vidljive točke"
      >
        <Ikona naziv="cilj" velicina={17} />
      </button>
      {greskaPodloge && (
        <div className="greska-podloge" role="status">
          <p>
            Podloga se nije učitala. Provjeri vezu ili odaberi drugu podlogu.
          </p>
          {aktivnaPodloga !== 'osm' && (
            <button onClick={() => postaviAktivnuPodlogu('osm')}>
              Prikaži uličnu kartu
            </button>
          )}
        </div>
      )}
      <span className="broj-tocaka">
        {smjestene.length} od {tocke.length} zapisa na karti
      </span>
    </div>
  );
}
